import { useEffect, useRef, useState } from "react";

/** ---------------------- Tipos ---------------------- */
export type Produto = {
  codigo: string;
  ncm: string;
  nome: string;
  classificacao: string;
  posicao: string;
  quantidade: number; // ← de quantidade_atual
  custo_unitario: string | null; // ← de custo_unitario_atual (formatado com 2 casas)
  margem_percentual: string; // ← derivado de margens[0] ou "0"
  vendavel: string; // ← "Sim" | "Não" | "N/A"
  moeda: string | null; // ← BRL | USD | EUR | ... (de estoque_atual.moeda)
};

// payload que chega do backend
type MargemRaw = {
  margem_percentual?: number | string | null;
  definido_por_id?: number | null;
  data_definicao?: string | null;
};

type ProdutoRawBackend = {
  codigo: string;
  ncm: string;
  nome: string;
  classificacao: string;
  posicao: string;
  quantidade_atual?: number | string | null; // vem de estoque_atual
  custo_unitario_atual?: number | string | null; // vem de estoque_atual
  moeda?: string | null; // vem de estoque_atual
  vendavel?: boolean | null; // vem de produtos
  margens?: MargemRaw[]; // array opcional
  ocultar?: boolean;
};

type ListaResp = {
  fonte?: "cache" | "banco";
  versao?: number | null;
  data: Produto[]; // já mapeado para o shape usado na UI
};

type AwaitResult = { changed: boolean; versao: number | null };

/** ---------------------- Constantes ---------------------- */
const BASE = "https://api.eva-googlemarine.com/departamentos/diretoria";
const URL = `${BASE}/estoque`;
const URL_AWAIT = `${BASE}/produtos/await`;

const POLL_FALLBACK_MS = 15000;
const ERROR_BACKOFF_MS = 5000;
const AWAIT_TIMEOUT_MS = 3000;
const AWAIT_GAP_MS = 120;

/** ---------------------- Utils ---------------------- */
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

function produtosMudaram(a: Produto[], b: Produto[]): boolean {
  if (a.length !== b.length) return true;
  return JSON.stringify(a) !== JSON.stringify(b);
}

// formata número para 2 casas; se inválido, retorna null
const toMoney = (v: unknown): string | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : null;
};

// boolean → "Sim" | "Não" | "N/A"
const boolToLabel = (b: unknown): string =>
  b === true ? "Sim" : b === false ? "Não" : "N/A";

// pega a margem mais recente; se não existir, "0"
const pickLatestMargin = (arr?: MargemRaw[]): string => {
  if (!Array.isArray(arr) || arr.length === 0) return "0";
  const m = arr[0]?.margem_percentual;
  if (m === null || m === undefined || m === "") return "0";
  const n = Number(m);
  return Number.isFinite(n) ? String(n) : String(m);
};

// normaliza texto pra comparação
const norm = (s: unknown) =>
  String(s ?? "")
    .trim()
    .toUpperCase();

/**
 * ✅ Regra solicitada:
 * - NÃO retornar produtos cuja classificacao === "LABORATORIO"
 *   (comparação case-insensitive e com trim)
 */
function isBlockedClassificacao(classificacao: unknown): boolean {
  return norm(classificacao) === "LABORATORIO";
}

/** ---------------------- HTTP helpers ---------------------- */
async function headVersao(signal?: AbortSignal): Promise<number | null> {
  try {
    const res = await fetch(URL, {
      method: "HEAD",
      headers: { Accept: "application/json" },
      signal,
    });
    if (!res.ok) return null;
    const v = res.headers.get("x-cache-ver");
    const n = v ? Number(v) : null;
    return Number.isFinite(n!) ? n : null;
  } catch {
    return null;
  }
}

async function getLista(
  _versaoAtual: number | null,
  _signal?: AbortSignal
): Promise<ListaResp> {
  const headers: Record<string, string> = { Accept: "application/json" };

  const res = await fetch(URL, {
    method: "GET",
    headers,
    cache: "no-store",
  });

  if (!res.ok) throw new Error(`Erro HTTP ${res.status}`);

  const fonte = (res.headers.get("x-source") as "cache" | "banco") || undefined;
  const vh = res.headers.get("x-cache-ver");
  const verHdr = vh ? Number(vh) : null;

  const json = await res.json();
  const verBody = typeof json?.versao === "number" ? json.versao : null;
  const versao = Number.isFinite(verHdr!) ? verHdr : verBody;

  // Preferir 'visiveis' quando o backend separar; senão usar 'data'
  const visiveisRaw = Array.isArray(json?.visiveis)
    ? (json.visiveis as ProdutoRawBackend[])
    : undefined;

  const dataRaw: ProdutoRawBackend[] = visiveisRaw ?? json?.data ?? [];

  // Se não veio 'visiveis', filtra localmente ocultos
  const onlyVisible: ProdutoRawBackend[] =
    visiveisRaw != null
      ? visiveisRaw
      : dataRaw.filter((p) => p?.ocultar !== true);

  // ✅ NOVO: filtra também por classificacao != "LABORATORIO"
  const onlyAllowed: ProdutoRawBackend[] = onlyVisible.filter(
    (p) => !isBlockedClassificacao(p?.classificacao)
  );

  // Mapeamento final para o shape Produto da UI
  const data: Produto[] = onlyAllowed.map((r) => {
    const moeda =
      typeof r.moeda === "string" && r.moeda.trim() !== ""
        ? r.moeda.trim().toUpperCase()
        : null;

    return {
      codigo: r.codigo,
      ncm: r.ncm,
      nome: r.nome,
      classificacao: r.classificacao,
      posicao: r.posicao,
      quantidade: Number(r.quantidade_atual ?? 0) || 0,
      custo_unitario: toMoney(r.custo_unitario_atual),
      margem_percentual: pickLatestMargin(r.margens),
      vendavel: boolToLabel(r.vendavel),
      moeda,
    };
  });

  return {
    fonte,
    versao: versao ?? null,
    data,
  };
}

async function awaitChange(
  since: number,
  signal?: AbortSignal
): Promise<AwaitResult | null> {
  try {
    const url = `${URL_AWAIT}?since=${since}&timeout_ms=${AWAIT_TIMEOUT_MS}`;
    const res = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      signal,
    });
    if (!res.ok) return null;

    const json = await res.json();
    const versao =
      typeof json?.versao === "number" ? (json.versao as number) : since;
    const changed = Boolean(json?.changed);

    return { changed, versao };
  } catch {
    return null;
  }
}

/** ---------------------- Hook ---------------------- */
export function useProdutosDisponiveis() {
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [loading, setLoading] = useState(true);
  const [fonte, setFonte] = useState<"cache" | "banco" | undefined>(undefined);
  const [versao, _setVersao] = useState<number | null>(null);

  const versaoRef = useRef<number | null>(null);
  const stop = useRef(false);

  const fetchingRef = useRef(false);
  const awaitCtrlRef = useRef<AbortController | null>(null);
  const awaitSupported = useRef<boolean | null>(null);
  const awaitInFlightRef = useRef(false);

  const setVersao = (v: number | null) => {
    versaoRef.current = v;
    _setVersao(v);
  };

  async function carregarLista(cur: number | null) {
    fetchingRef.current = true;
    try {
      const lista = await getLista(cur /* sem signal */);
      if (lista.versao != null) setVersao(lista.versao);
      setProdutos((prev) =>
        produtosMudaram(prev, lista.data) ? lista.data : prev
      );
      setFonte(lista.fonte);
    } finally {
      fetchingRef.current = false;
    }
  }

  useEffect(() => {
    stop.current = false;

    (async () => {
      try {
        const vHead = await headVersao(); // opcional
        await carregarLista(vHead ?? null);
        setLoading(false);

        while (!stop.current) {
          // Evita GET simultâneo
          while (fetchingRef.current && !stop.current) {
            await sleep(25);
          }
          if (stop.current) break;

          const cur = versaoRef.current;

          // ---------- Long-poll preferencial ----------
          if (awaitSupported.current !== false && cur != null) {
            if (awaitInFlightRef.current) {
              await sleep(50);
              continue;
            }

            const acAwait = new AbortController();
            awaitCtrlRef.current = acAwait;
            awaitInFlightRef.current = true;

            let resp: AwaitResult | null = null;
            try {
              resp = await awaitChange(cur, acAwait.signal);
            } catch (e: any) {
              if (e?.name !== "AbortError") {
                await sleep(ERROR_BACKOFF_MS);
              }
            } finally {
              if (awaitCtrlRef.current === acAwait) awaitCtrlRef.current = null;
              awaitInFlightRef.current = false;
            }

            if (stop.current) break;

            if (resp === null) {
              if (awaitSupported.current === null) {
                awaitSupported.current = false;
              }
              await sleep(POLL_FALLBACK_MS);
              continue;
            }

            const avancou = (resp.versao ?? -1) > cur;
            if (resp.changed || avancou) {
              await carregarLista(cur);
              await sleep(20);
            } else {
              await sleep(AWAIT_GAP_MS);
            }

            continue;
          }

          // --------- Fallback por HEAD/ETag ---------
          const vNow = await headVersao();
          const cur2 = versaoRef.current;

          if (vNow !== null && cur2 !== null && vNow === cur2) {
            await sleep(POLL_FALLBACK_MS);
            continue;
          }
          if (vNow === null && cur2 !== null) {
            await sleep(POLL_FALLBACK_MS);
          }

          await carregarLista(cur2);
        }
      } catch (e) {
        if (!stop.current)
          console.error("useProdutosDisponiveis loop error:", e);
        setLoading(false);
      }
    })();

    return () => {
      stop.current = true;
      awaitCtrlRef.current?.abort();
    };
  }, []);

  const refresh = async () => {
    while (fetchingRef.current) await sleep(25);
    await carregarLista(versaoRef.current);
  };

  return { produtos, loading, refresh, fonte, versao };
}
